import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setUserData } from '@/reducers/users';
import {
  Play,
  Pause,
  RotateCcw,
  Sparkles,
  Zap,
  Video,
  Clock,
  Type,
  Film,
  Wand2,
  AlertCircle,
  CheckCircle2,
  Download,
  Share2,
  Loader2,
} from 'lucide-react';
import AiLabPageShell from '@/components/ai-lab-page-shell/AiLabPageShell';
import axios from '@/api/ai-axios';
import useAuth from '@/hooks/useAuth';
import styles from './VideoDemo.module.scss';

const VIDEO_TEMPLATES = [
  {
    category: '校园生活',
    items: [
      '清晨的校园里，阳光透过树叶洒下斑驳光影，同学们背着书包走进教室，开始新的一天。',
      '操场上，同学们在快乐地奔跑、跳跃，欢呼声和笑声回荡在整个校园。',
      '图书馆里安静而温馨，书页翻动的声音像一首轻柔的乐曲。',
    ],
  },
  {
    category: '科普世界',
    items: [
      '太阳系有八大行星，地球是我们美丽的家园，月亮围绕地球旋转。',
      '小蜜蜂在花丛中忙碌地采蜜，它们跳起8字舞告诉同伴花蜜在哪里。',
      '恐龙曾经是地球的霸主，有的体型巨大如山，有的身手敏捷如豹。',
    ],
  },
  {
    category: '童话故事',
    items: [
      '小种子在泥土里慢慢发芽，迎着阳光长成了一棵参天大树。',
      '小兔子提着篮子去森林里采蘑菇，遇到了一只友善的小松鼠。',
      '星星在夜空中眨着眼睛，月亮婆婆给小朋友们讲着晚安故事。',
    ],
  },
];

const COST_MAP = {
  5: 250,
  10: 500,
};

const DURATION_TO_FRAMES_MAP = {
  5: '121',
  10: '241',
};

const POLL_INTERVAL = 3000;

const alertBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  borderRadius: '12px',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px solid',
};

const successBoxStyle = {
  ...alertBoxStyle,
  backgroundColor: '#ecfdf5',
  color: '#047857',
  borderColor: '#a7f3d0',
};

const errorBoxStyle = {
  ...alertBoxStyle,
  backgroundColor: '#fef2f2',
  color: '#b91c1c',
  borderColor: '#fecaca',
};

function VideoDemo() {
  const { checkLogin } = useAuth();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.users.data);
  const uid = useSelector((state) => state.users.uid);
  const points = userData?.quotaInfo?.total_balance ?? 0;

  const [text, setText] = useState(
    '小种子在泥土里慢慢发芽，迎着阳光长成了一棵参天大树。',
  );
  const [duration, setDuration] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [taskId, setTaskId] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [message, setMessage] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const videoRef = useRef(null);
  const pollingRef = useRef(null);
  const mountedRef = useRef(true);
  const blobUrlRef = useRef('');

  const cost = COST_MAP[duration];

  // 释放旧 blob URL
  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = '';
    }
  }, []);

  // 刷新用户余额
  const refreshUserBalance = useCallback(async () => {
    try {
      const res = await axios.post('/api/user/getUserInfo', {
        secret: 'zmrobo',
      });
      if (res.status === 1 && mountedRef.current) {
        dispatch(setUserData(res.data));
      }
    } catch {
      // 静默失败，不影响用户体验
    }
  }, [dispatch]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) clearTimeout(pollingRef.current);
      revokeBlobUrl();
    };
  }, [revokeBlobUrl]);

  // 同步 video 播放状态
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoUrl) return;
    if (isPlaying) {
      el.play().catch(() => setIsPlaying(false));
    } else {
      el.pause();
    }
  }, [isPlaying, videoUrl]);

  // video 原生事件同步
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onEnd = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    el.addEventListener('ended', onEnd);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, [videoUrl]);

  const showMsg = (type, textMsg) => {
    setMessage({ type, text: textMsg });
    setTimeout(() => setMessage(null), 3000);
  };

  // 通过 fetch（no-referrer）下载视频转 blob URL，绕过 CDN 防盗链
  const loadVideoAsBlob = useCallback(
    async (url) => {
      if (!mountedRef.current) return '';
      setIsLoadingVideo(true);
      try {
        const resp = await fetch(url, {
          referrerPolicy: 'no-referrer',
          credentials: 'omit',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        if (mountedRef.current) {
          revokeBlobUrl();
          blobUrlRef.current = blobUrl;
          setVideoUrl(blobUrl);
          setIsPlaying(true);
        }
        return blobUrl;
      } catch (err) {
        // 降级：直接用原 URL（可能也不行，但试试）
        if (mountedRef.current) {
          setVideoUrl(url);
          setIsPlaying(true);
        }
        return url;
      } finally {
        if (mountedRef.current) setIsLoadingVideo(false);
      }
    },
    [revokeBlobUrl],
  );

  const pollResult = useCallback(
    async (tid) => {
      if (!mountedRef.current) return;

      try {
        const form = new FormData();
        form.append('task_id', tid);

        const res = await axios.post('/api/volc_t2v_get_result', form);

        if (!mountedRef.current) return;

        if (res.status === 1) {
          const data = res.data;
          if (data.status === 'done' && data.video_url) {
            setProgress(100);
            setIsGenerating(false);
            showMsg('success', '视频生成成功！积分已自动扣除');
            // 刷新用户余额
            refreshUserBalance();
            // 通过 blob 代理加载视频
            loadVideoAsBlob(data.video_url);
            return;
          }
          if (data.status === 'failed') {
            setIsGenerating(false);
            showMsg('error', '视频生成失败，请稍后重试');
            return;
          }
          // still generating — continue polling
          pollingRef.current = setTimeout(() => pollResult(tid), POLL_INTERVAL);
        } else {
          setIsGenerating(false);
          showMsg('error', res.message || '查询失败');
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setIsGenerating(false);
        showMsg('error', err.message || '网络错误');
      }
    },
    [loadVideoAsBlob, refreshUserBalance],
  );

  const handleGenerate = async () => {
    if (!text.trim()) {
      showMsg('error', '请输入视频文案');
      return;
    }
    if (!uid) {
      checkLogin();
      return;
    }
    if (points < cost) {
      showMsg('error', `积分不足！当前积分 ${points}，需要 ${cost} 积分`);
      return;
    }

    // 清理旧播放
    if (pollingRef.current) clearTimeout(pollingRef.current);
    revokeBlobUrl();
    setVideoUrl('');
    setTaskId('');
    setIsPlaying(false);
    setIsGenerating(true);
    setProgress(0);

    try {
      const form = new FormData();
      form.append('prompt', text.trim());
      form.append('frames', DURATION_TO_FRAMES_MAP[duration]);

      const res = await axios.post('/api/volc_t2v_submit', form);

      if (!mountedRef.current) return;

      if (res.status === 1 && res.data?.task_id) {
        const tid = res.data.task_id;
        setTaskId(tid);
        setProgress(5);
        // start polling
        pollingRef.current = setTimeout(() => pollResult(tid), POLL_INTERVAL);
      } else {
        setIsGenerating(false);
        showMsg('error', res.message || '提交失败');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setIsGenerating(false);
      showMsg('error', err.message || '网络错误');
    }
  };

  const handleReplay = () => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  const handleDownload = async () => {
    if (!videoUrl) return;
    try {
      const resp = await fetch(videoUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-video-${taskId.slice(-8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showMsg('success', '视频已下载');
    } catch {
      window.open(videoUrl, '_blank');
    }
  };

  const handleShare = async () => {
    if (!videoUrl) return;
    try {
      await navigator.clipboard.writeText(videoUrl);
      showMsg('success', '视频链接已复制到剪贴板！');
    } catch {
      showMsg('error', '复制失败，请手动复制');
    }
  };

  return (
    <AiLabPageShell title="AI视频创作">
      <div className={styles.wrapper}>
        {message && (
          <div
            style={message.type === 'success' ? successBoxStyle : errorBoxStyle}
          >
            {message.type === 'success' ? (
              <CheckCircle2 size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            {message.text}
          </div>
        )}

        <div className={styles.grid}>
          {/* Left: Configuration */}
          <div className={styles.leftCol}>
            {/* Text input */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Type size={18} style={{ color: '#7c3aed' }} />
                <h3 className={styles.cardTitle}>视频文案</h3>
                <span className={styles.charCount}>{text.length} 字</span>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                className={styles.textarea}
                placeholder="描述你想让AI生成的视频画面..."
              />
              <div style={{ position: 'relative', marginTop: '8px' }}>
                <button
                  onClick={() => setShowTemplates((s) => !s)}
                  className={styles.templateToggle}
                >
                  <Sparkles size={12} />
                  {showTemplates ? '收起模板' : '使用模板快速填充'}
                </button>
                {showTemplates && (
                  <div className={styles.templateDropdown}>
                    {VIDEO_TEMPLATES.map((cat) => (
                      <div
                        key={cat.category}
                        className={styles.templateCategory}
                      >
                        <p className={styles.templateCatTitle}>
                          {cat.category}
                        </p>
                        <div className={styles.templateItems}>
                          {cat.items.map((item, i) => (
                            <button
                              key={i}
                              onClick={() => {
                                setText(item);
                                setShowTemplates(false);
                              }}
                              className={styles.templateItem}
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Duration selection */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Clock size={18} style={{ color: '#7c3aed' }} />
                <h3 className={styles.cardTitle}>视频时长</h3>
              </div>
              <div className={styles.durationGrid}>
                {[5, 10].map((sec) => (
                  <button
                    key={sec}
                    onClick={() => setDuration(sec)}
                    className={`${styles.durationBtn} ${duration === sec ? styles.durationBtnActive : ''}`}
                  >
                    <Film
                      size={20}
                      style={{
                        color: duration === sec ? '#7c3aed' : '#9ca3af',
                      }}
                    />
                    <span className={styles.durationLabel}>{sec} 秒</span>
                    <span className={styles.durationCost}>
                      消耗 {COST_MAP[sec]} 积分
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || isLoadingVideo}
              className={styles.generateBtn}
            >
              {isGenerating ? (
                <>
                  <Wand2 size={18} className={styles.spin} />
                  AI 正在创作中...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  生成视频（消耗 {cost} 积分）
                </>
              )}
            </button>
          </div>

          {/* Right: Preview / Result */}
          <div className={styles.rightCol}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Video size={18} style={{ color: '#7c3aed' }} />
                <h3 className={styles.cardTitle}>视频预览</h3>
                {videoUrl && (
                  <span className={styles.badge}>
                    即梦文生视频 · {duration}秒
                  </span>
                )}
              </div>

              <div className={styles.previewArea}>
                {isGenerating ? (
                  <div className={styles.generatingOverlay}>
                    <div className={styles.generatingIcon}>
                      <Wand2 size={32} className={styles.pulse} />
                    </div>
                    <p className={styles.generatingTitle}>
                      AI 正在生成你的视频...
                    </p>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className={styles.progressText}>{progress}%</p>
                    <p className={styles.generatingSubtitle}>
                      {taskId
                        ? '火山引擎即梦模型生成中，请稍候...'
                        : '正在提交任务...'}
                    </p>
                  </div>
                ) : isLoadingVideo ? (
                  <div className={styles.generatingOverlay}>
                    <div className={styles.generatingIcon}>
                      <Loader2 size={32} className={styles.spin} />
                    </div>
                    <p className={styles.generatingTitle}>正在加载视频...</p>
                    <p className={styles.generatingSubtitle}>下载中，请稍候</p>
                  </div>
                ) : videoUrl ? (
                  <div className={styles.videoPlayer}>
                    <div className={styles.canvasWrapper}>
                      <video
                        ref={videoRef}
                        src={videoUrl}
                        className={styles.canvas}
                        playsInline
                      />
                    </div>
                    <div className={styles.videoControls}>
                      <div className={styles.controlsLeft}>
                        <button
                          onClick={() => setIsPlaying((p) => !p)}
                          className={styles.playBtn}
                        >
                          {isPlaying ? (
                            <Pause size={18} fill="currentColor" />
                          ) : (
                            <Play
                              size={18}
                              fill="currentColor"
                              style={{ marginLeft: '2px' }}
                            />
                          )}
                        </button>
                        <button
                          onClick={handleReplay}
                          className={styles.replayBtn}
                          title="重播"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                      <div className={styles.videoLabel}>AI 生成视频</div>
                      <div className={styles.controlsRight}>
                        <button
                          onClick={handleShare}
                          className={styles.iconBtn}
                          title="分享"
                        >
                          <Share2 size={15} />
                        </button>
                        <button
                          onClick={handleDownload}
                          className={styles.iconBtn}
                          title="下载"
                        >
                          <Download size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Film size={36} />
                    </div>
                    <p className={styles.emptyTitle}>视频将在这里预览</p>
                    <p className={styles.emptyDesc}>
                      在左侧输入文案，点击「生成视频」即可开始创作
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Educational tip */}
        <div className={styles.tipCard}>
          <div className={styles.tipIcon}>
            <Zap size={20} style={{ color: '#7c3aed' }} />
          </div>
          <div>
            <h4 className={styles.tipTitle}>小知识：AI 是怎么生成视频的？</h4>
            <p className={styles.tipText}>
              AI
              视频生成技术叫做「文生视频」（Text-to-Video）。你输入一段文字描述，AI
              会先
              <b>理解</b>文字的意思，然后<b>想象</b>出对应的画面，再一帧一帧地
              <b>绘制</b>
              出来，最后连成一段流畅的视频。就像一位超级厉害的画家，能在几秒钟内画出几百张画！
            </p>
          </div>
        </div>
      </div>
    </AiLabPageShell>
  );
}

export default VideoDemo;
